import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSubjectFeesEntryScreen } from './hooks/useSubjectFeesEntryScreen';
import { appToast } from '../../../components/AppToast';

function TextField({
  label,
  value,
  onChange,
  placeholder,
  required = false,
  type = 'text',
  min,
  step,
}) {
  return (
    <label className="grid gap-2">
      <span className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
        {label}
        {required ? <span className="text-error"> *</span> : null}
      </span>
      <input
        type={type}
        min={min}
        step={step}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 py-3.5 text-sm text-on-surface outline-none placeholder:text-on-surface-variant/60 transition-all focus:border-primary focus:ring-1 focus:ring-primary/20 shadow-2xs"
      />
    </label>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options = [],
  placeholder = 'Select option...',
  required = false,
}) {
  return (
    <label className="grid gap-2">
      <span className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
        {label}
        {required ? <span className="text-error"> *</span> : null}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 py-3.5 text-sm text-on-surface outline-none transition-all focus:border-primary focus:ring-1 focus:ring-primary/20 shadow-2xs cursor-pointer"
      >
        <option value="">{placeholder}</option>
        {options.map((opt) => (
          <option key={opt.id} value={opt.id}>
            {opt.name}
          </option>
        ))}
      </select>
    </label>
  );
}

export default function SubjectFeesEntryScreen() {
  const { state, actions } = useSubjectFeesEntryScreen();

  useEffect(() => {
    if (state.error) appToast.error(state.error);
  }, [state.error]);

  useEffect(() => {
    if (state.success) appToast.success(state.success);
  }, [state.success]);

  const formattedTotal = Number(state.totalFees || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return (
    <div className="mx-auto flex w-full max-w-[1320px] flex-col gap-6 pb-12">
      {/* Top Header */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold text-on-surface-variant mb-1">
            <span>Academics</span>
            <span className="material-symbols-outlined text-[12px]">chevron_right</span>
            <Link to="/masters/subject-fees/list" className="hover:text-primary transition-colors">
              Subjectwise Fees Registry
            </Link>
            <span className="material-symbols-outlined text-[12px]">chevron_right</span>
            <span className="text-primary font-bold">{state.isEditMode ? 'Edit' : 'Create'}</span>
          </div>
          <h1 className="font-display text-4xl font-extrabold tracking-[-0.02em] text-on-surface">
            {state.isEditMode ? 'Edit Subject Fee Schedule' : 'Create Subject Fee Schedule'}
          </h1>
          <p className="mt-1 text-xs text-on-surface-variant font-medium">
            Configure course, subject, and fee schedule details for an academic year.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Link
            to="/masters/subject-fees/list"
            className="inline-flex items-center justify-center rounded-xl bg-surface-container-high px-5 py-2.5 text-xs font-bold text-on-surface transition hover:bg-surface-container-highest cursor-pointer"
          >
            Discard
          </Link>
          <button
            type="button"
            onClick={actions.handleSave}
            disabled={state.isSaving}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-6 py-2.5 text-xs font-bold text-on-primary shadow-sm hover:brightness-105 active:scale-[0.98] transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {state.isSaving ? (
              <>
                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-on-primary border-t-transparent" />
                <span>Saving...</span>
              </>
            ) : (
              <span>{state.isEditMode ? 'Update Schedule' : 'Save Schedule'}</span>
            )}
          </button>
        </div>
      </section>

      {state.isLoading ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-outline-variant/30 bg-surface-container-lowest px-6 py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
          <span className="mt-3 text-xs font-medium text-on-surface-variant">
            Loading fee schedule details...
          </span>
        </div>
      ) : (
        <section className="grid grid-cols-12 gap-6">
          {/* Main Left Column */}
          <div className="col-span-12 space-y-6 lg:col-span-8">
            {/* Academic Identification Card */}
            <article className="rounded-2xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-sm">
              <div className="mb-6 flex items-center gap-3">
                <div className="h-6 w-1 rounded-full bg-primary" />
                <h2 className="text-lg font-bold text-on-surface">Academic Identification</h2>
              </div>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                <div>
                  <TextField
                    label="Academic Year"
                    required
                    placeholder="e.g. 2024-2025"
                    value={state.form.ayear || ''}
                    onChange={(val) => actions.patchForm('ayear', val)}
                  />
                </div>

                <div>
                  <SelectField
                    label="Course"
                    required
                    placeholder="Select Course..."
                    value={state.form.course_id || ''}
                    options={state.courseOptions}
                    onChange={(val) => actions.patchForm('course_id', val)}
                  />
                </div>

                <div>
                  <SelectField
                    label="Subject"
                    required
                    placeholder="Select Subject..."
                    value={state.form.subject_id || ''}
                    options={state.subjectOptions}
                    onChange={(val) => actions.patchForm('subject_id', val)}
                  />
                </div>
              </div>
            </article>

            {/* Fee Breakdown Card */}
            <article className="rounded-2xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-sm">
              <div className="mb-6 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-6 w-1 rounded-full bg-primary" />
                  <h2 className="text-lg font-bold text-on-surface">Fee Structure Breakdown</h2>
                </div>
                <div className="flex items-center gap-2 rounded-xl bg-primary/10 px-3.5 py-1.5 text-xs font-bold text-primary">
                  <span>Computed Total:</span>
                  <span className="font-mono text-sm font-extrabold">₹ {formattedTotal}</span>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                <div>
                  <TextField
                    label="Admission Fees (₹)"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={state.form.fees_admission ?? ''}
                    onChange={(val) => actions.patchForm('fees_admission', val)}
                  />
                </div>

                <div>
                  <TextField
                    label="Tuition Fees (₹)"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={state.form.fees_tuition ?? ''}
                    onChange={(val) => actions.patchForm('fees_tuition', val)}
                  />
                </div>

                <div>
                  <TextField
                    label="Courseware Fees (₹)"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={state.form.fees_courseware ?? ''}
                    onChange={(val) => actions.patchForm('fees_courseware', val)}
                  />
                </div>
              </div>
            </article>
          </div>

          {/* Side Right Column */}
          <div className="col-span-12 space-y-6 lg:col-span-4">
            {/* Operational Status Card */}
            <article className="rounded-2xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-sm">
              <div className="mb-4 flex items-center gap-3">
                <div className="h-6 w-1 rounded-full bg-primary" />
                <h3 className="text-base font-bold text-on-surface">Operational Status</h3>
              </div>

              <div className="flex rounded-xl bg-surface-container-high p-1">
                <button
                  type="button"
                  onClick={() => actions.setOperationalStatus('active')}
                  className={`flex-1 rounded-lg py-2 text-xs font-bold transition-all cursor-pointer select-none ${
                    state.operationalStatus === 'active'
                      ? 'bg-primary text-on-primary shadow-xs'
                      : 'text-on-surface-variant hover:text-on-surface'
                  }`}
                >
                  Active
                </button>
                <button
                  type="button"
                  onClick={() => actions.setOperationalStatus('inactive')}
                  className={`flex-1 rounded-lg py-2 text-xs font-bold transition-all cursor-pointer select-none ${
                    state.operationalStatus === 'inactive'
                      ? 'bg-primary text-on-primary shadow-xs'
                      : 'text-on-surface-variant hover:text-on-surface'
                  }`}
                >
                  Archived
                </button>
              </div>

              <p className="mt-3 text-[11px] leading-relaxed text-on-surface-variant">
                {state.operationalStatus === 'active'
                  ? 'Fee schedule is active and available for billing and admissions.'
                  : 'Archived schedules remain preserved for historical audit trails.'}
              </p>
            </article>

            {/* Fee Schedule Summary Card */}
            <article className="rounded-2xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-sm">
              <div className="mb-4 flex items-center gap-3">
                <div className="h-6 w-1 rounded-full bg-primary" />
                <h3 className="text-base font-bold text-on-surface">Schedule Summary</h3>
              </div>

              <div className="space-y-3 text-xs">
                <div className="flex items-center justify-between border-b border-outline-variant/15 pb-2">
                  <span className="text-on-surface-variant">Admission Fees:</span>
                  <span className="font-mono font-semibold text-on-surface">
                    ₹ {Number(state.form.fees_admission || 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center justify-between border-b border-outline-variant/15 pb-2">
                  <span className="text-on-surface-variant">Tuition Fees:</span>
                  <span className="font-mono font-semibold text-on-surface">
                    ₹ {Number(state.form.fees_tuition || 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center justify-between border-b border-outline-variant/15 pb-2">
                  <span className="text-on-surface-variant">Courseware Fees:</span>
                  <span className="font-mono font-semibold text-on-surface">
                    ₹ {Number(state.form.fees_courseware || 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center justify-between pt-1">
                  <span className="font-bold text-on-surface">Total Amount:</span>
                  <span className="font-mono text-sm font-extrabold text-primary">
                    ₹ {formattedTotal}
                  </span>
                </div>
              </div>
            </article>
          </div>
        </section>
      )}
    </div>
  );
}
